import React, { createContext, useContext, useEffect, useReducer, useState } from 'react';
import { api } from '../api/client';

/* ============================================================
   CartContext — Gestão global do carrinho
   ============================================================
   Funcionalidades:
   • addItem(product, qty)    — adiciona / incrementa
   • removeItem(id)           — remove artigo
   • updateQty(id, qty)       — altera quantidade
   • clearCart()              — esvazia carrinho
   • totalItems               — nº total de unidades
   • subtotal                 — valor em euros (number)
   • subtotalFormatted        — valor formatado "€xx,xx"
   • Persistência em localStorage
   ============================================================ */

const STORAGE_KEY = 'quinta-cart-v1';

// Encomendas só saem em caixas fechadas de 6 garrafas — a quantidade total
// (soma de todos os artigos, qualquer mistura de vinhos) tem de ser um
// múltiplo de 6. Aplicado tanto no carrinho/checkout (aviso) como no
// backend (validação real — ver StoreOrderRequest::withValidator()).
export const MIN_ORDER_QTY = 6;

// Portes: preço fixo por conjunto de 6 garrafas, não por valor de compra.
// Os valores reais são definidos no admin (/admin/shipping-settings) e
// carregados via GET /shipping-rates (ver fetchShippingRates no
// CartProvider abaixo) — estes são só o fallback usado enquanto esse
// pedido ainda não respondeu ou se falhar.
export const DEFAULT_SHIPPING_RATES = { pt: 7.10, intl: 150.00 };

// countryCode pode ser null/undefined (ex. no carrinho, antes de se
// escolher o país no checkout) — nesse caso devolve null para o chamador
// decidir como mostrar "por calcular" em vez de arriscar um valor errado
// (a diferença entre PT e o resto é enorme).
export function calculateShipping(totalItems, countryCode, rates = DEFAULT_SHIPPING_RATES) {
  if (!totalItems || totalItems <= 0) return 0;
  if (!countryCode) return null;
  const sets = Math.ceil(totalItems / MIN_ORDER_QTY);
  const rate = countryCode === 'pt' ? rates.pt : rates.intl;
  return sets * rate;
}

const CartContext = createContext(null);

/* ── Utilitários ────────────────────────────────────────── */

// Converte "€24,90" → 24.90
export function parsePrice(str) {
  if (typeof str === 'number') return str;
  if (!str) return 0;
  const clean = String(str).replace(/[^\d,.-]/g, '').replace(',', '.');
  const n = parseFloat(clean);
  return isNaN(n) ? 0 : n;
}

// Formata 24.9 → "€24,90"
export function formatPrice(n) {
  return `€${n.toFixed(2).replace('.', ',')}`;
}

/* ── Reducer ────────────────────────────────────────────── */

const initialState = { items: [] };

function reducer(state, action) {
  switch (action.type) {
    case 'HYDRATE': {
      return action.payload || initialState;
    }

    case 'ADD': {
      const { product, qty } = action.payload;
      const existing = state.items.find(i => i.id === product.id);
      let items;
      if (existing) {
        items = state.items.map(i =>
          i.id === product.id ? { ...i, qty: i.qty + qty } : i
        );
      } else {
        items = [...state.items, { ...product, qty }];
      }
      return { ...state, items };
    }

    case 'UPDATE_QTY': {
      const { id, qty } = action.payload;
      if (qty <= 0) {
        return { ...state, items: state.items.filter(i => i.id !== id) };
      }
      return {
        ...state,
        items: state.items.map(i => (i.id === id ? { ...i, qty } : i)),
      };
    }

    case 'REMOVE': {
      return {
        ...state,
        items: state.items.filter(i => i.id !== action.payload.id),
      };
    }

    case 'CLEAR': {
      return initialState;
    }

    default:
      return state;
  }
}

/* ── Provider ───────────────────────────────────────────── */

export function CartProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [shippingRates, setShippingRates] = useState(DEFAULT_SHIPPING_RATES);

  // Carrega os valores reais de portes definidos no admin. Se falhar,
  // mantém o fallback (DEFAULT_SHIPPING_RATES) em vez de bloquear o
  // carrinho/checkout — o valor final é sempre recalculado no servidor
  // no momento da encomenda, isto é só para a pré-visualização.
  useEffect(() => {
    let cancelled = false;
    api.getShippingRates()
      .then((data) => { if (!cancelled && data) setShippingRates(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  // Precisa de ser estado, não ref: uma ref muda de valor de imediato,
  // dentro do mesmo ciclo de efeitos, antes do re-render que aplica o
  // HYDRATE — o efeito de persistência abaixo ainda veria a ref já a
  // "true" mas o `state` still como initialState nesse mesmo ciclo, e
  // gravava o carrinho vazio por cima do que estava guardado. Com
  // useState, o valor só muda a partir do próximo render, que já reflete
  // o HYDRATE.
  const [hasHydrated, setHasHydrated] = useState(false);

  // Hidratar do localStorage no mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        dispatch({ type: 'HYDRATE', payload: parsed });
      }
    } catch (e) {
      console.warn('Erro ao ler carrinho do localStorage', e);
    } finally {
      setHasHydrated(true);
    }
  }, []);

  // Persistir a cada alteração (só depois de hidratar — ver nota acima)
  useEffect(() => {
    if (!hasHydrated) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      console.warn('Erro ao guardar carrinho', e);
    }
  }, [state, hasHydrated]);

  /* — Actions — */
  const addItem = (product, qty = 1) =>
    dispatch({ type: 'ADD', payload: { product, qty } });

  const updateQty = (id, qty) =>
    dispatch({ type: 'UPDATE_QTY', payload: { id, qty } });

  const removeItem = (id) =>
    dispatch({ type: 'REMOVE', payload: { id } });

  const clearCart = () => dispatch({ type: 'CLEAR' });

  /* — Derivados — */
  const totalItems = state.items.reduce((sum, i) => sum + i.qty, 0);

  const subtotal = state.items.reduce(
    (sum, i) => sum + parsePrice(i.price) * i.qty,
    0
  );

  const subtotalFormatted = formatPrice(subtotal);

  const qtyRemainder = totalItems % MIN_ORDER_QTY;
  const isValidQuantity = totalItems > 0 && qtyRemainder === 0;
  // Quantas garrafas faltam para fechar a próxima caixa de 6 (0 se já for
  // válido ou o carrinho estiver vazio).
  const unitsNeededForValidQty = totalItems === 0 || qtyRemainder === 0
    ? 0
    : MIN_ORDER_QTY - qtyRemainder;

  const value = {
    items: state.items,
    addItem,
    updateQty,
    removeItem,
    clearCart,
    totalItems,
    subtotal,
    subtotalFormatted,
    isValidQuantity,
    unitsNeededForValidQty,
    shippingRates,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

/* ── Hook ───────────────────────────────────────────────── */

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error('useCart deve ser usado dentro de um <CartProvider>');
  }
  return ctx;
}
